/**
 * The editors, one per field type.
 *
 * The other half of the grid's design: read-only cells everywhere, and one
 * editor mounted over the active cell. That is what makes twenty-seven field
 * types tractable -- there is exactly one of these on screen at a time, so an
 * editor may be as expensive as the type needs, while a *cell* has to be cheap
 * enough to draw three and a half thousand of.
 *
 * The map is exhaustive over `FieldTypeName`, so a twenty-eighth type fails to
 * compile until it is answered for. `null` is a real answer and means the type
 * cannot be edited here: the cell still renders, still selects, and simply does
 * not open. Every one of them is now a type the server computes -- what began
 * as a list of remaining work is a list of things there is nothing to send for.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react'

import BigNumber from 'bignumber.js'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, FieldOf, FieldTypeName } from '@/api/types'
import type { FieldContext } from '../../behavior/behavior'
import { cn } from '@/shared/lib/utils'
import { Input } from '@/shared/ui/input'
import { parseBooleanValue } from '../../booleans'
import { parseRating, ratingColor, ratingMaxOf } from '../../rating'
import { ratingIcon } from '../rating'
import { LinkRowInput, normaliseLinks, parseLinks } from './link-row'
import { FileInput } from './file'
import { normaliseFiles, parseFiles } from '../../files'
import {
  formatDateForApi,
  formatDateValue,
  getDateHumanReadableFormat,
  getTimeHumanReadableFormat,
  parseDateInput,
} from '../../dates'
import {
  durationFormatOf,
  formatDurationValue,
  parseDurationValue,
  roundDurationValueToFormat,
} from '../../duration'
import { formatNumberValue, parseNumberValue } from '../../numbers'

/**
 * Moves the focus to whichever of a set of controls is the current one.
 *
 * Skipped on the very first run when the editor was not meant to take the
 * focus, and only then: a form mounts twenty of these at once, and every one
 * of them reaching for the focus leaves it on whichever mounted last. Once
 * somebody has moved within one, moving the focus with them is the whole
 * point of a roving tab stop.
 */
function useFocusOn(
  at: number,
  autoFocus: boolean,
  find: () => HTMLElement | null,
): void {
  const moved = useRef(false)
  useEffect(() => {
    const first = !moved.current
    moved.current = true
    if (first && !autoFocus) return
    find()?.focus()
    // Deliberately not `find`, which is a new closure on every render: this
    // must run when the position changes and not when anything else does, or
    // it would take the focus back on every keystroke elsewhere on the page.
  }, [at, autoFocus])
}

export interface CellEditorProps {
  field: Field
  value: string
  onValueChange: (value: string) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  label: string
  /** The lookups this field needs that are not on the row. */
  context: FieldContext
  /**
   * Whether the existing text should be selected when the editor takes focus.
   *
   * True when the editor was opened to edit what is there, false when it was
   * opened by typing over it -- the draft is already the new value then.
   */
  selectOnFocus?: boolean
  /**
   * Whether this editor should take the focus when it appears.
   *
   * True everywhere one editor is opened over a cell, which is what makes
   * typing continue where it left off. False in a form, where every field is
   * mounted at once and twenty editors each grabbing the focus would leave it
   * on whichever happened to mount last.
   */
  autoFocus: boolean
  /**
   * Whether what is in the editor is something the cell will not accept.
   *
   * Passed in rather than worked out here: the same answer decides whether the
   * editor may be closed, and two readings of it could disagree.
   */
  invalid?: boolean
}

export interface CellEditorType {
  /** What the editor shows for a stored value. */
  toDraft: (field: Field, value: unknown) => string
  /**
   * What is sent for what was typed.
   *
   * The context is what a collaborator field needs and no other type does: a
   * person is named by an id the field cannot resolve on its own, and a paste
   * carries their name rather than that id.
   */
  fromDraft: (field: Field, draft: string, context?: FieldContext) => unknown

  /**
   * What a paste of *text* means, when that is not what a draft means.
   *
   * The same question for every type but one. A link row's draft carries the
   * rows themselves, ids and all, because they are chosen from a list; a paste
   * carries the names somebody read on a screen, which only the server can turn
   * back into rows.
   */
  fromText?: (field: Field, text: string, context?: FieldContext) => unknown

  Component: (props: CellEditorProps) => ReactElement

  /**
   * True when this editor needs more room than a grid cell has.
   *
   * The grid draws its editor inside the scroll container, so anything taller
   * than a row is clipped at the bottom of the viewport. A field marked this
   * way opens the *row* instead, where there is room -- which is a better
   * answer than a picker somebody cannot see the bottom of.
   */
  needsRoom?: boolean
}

/**
 * A single-line text box, which is what every member of the text family needs.
 *
 * Long text wants a growing box eventually; it gets this one until it has one,
 * because a long text field that cannot be edited at all is worse than one
 * edited on a single line.
 */
function TextInput({
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
  selectOnFocus,
  invalid,
}: CellEditorProps): ReactElement {
  // Answered once, when the editor appears, and deliberately not re-read.
  //
  // Opened for editing, the existing text is selected, so the first thing typed
  // replaces it -- which is what Enter does to a cell in every spreadsheet.
  // Opened by typing a character the draft is already just that character, and
  // selecting it would make the *second* keystroke replace the first.
  //
  // The reason it is a ref and not the prop is that the prop is "is the draft
  // still what the cell holds", which becomes true again the moment somebody
  // types their way back to it: editing "Al" and typing "Alexander" is "Al"
  // after two keystrokes, and a re-select there swallows everything typed so
  // far. That reads as the application dropping characters.
  const selectOnOpen = useRef(selectOnFocus)

  return (
    <Input
      autoFocus={autoFocus}
      ref={(input) => {
        if (autoFocus && selectOnOpen.current === true) {
          selectOnOpen.current = false
          input?.select()
        }
      }}
      aria-label={label}
      aria-invalid={invalid === true}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value)
      }}
      onKeyDown={onKeyDown}
      className={cn(
        'h-full w-full rounded-none border-2 border-primary bg-background px-2 text-sm shadow-none focus-visible:ring-0',
        invalid === true && 'border-destructive',
      )}
    />
  )
}

const textEditor: CellEditorType = {
  /**
   * A cell with nothing in it edits as an empty box rather than as "null".
   *
   * Anything that is not a primitive edits as empty too. The text family only
   * ever stores strings, so this cannot happen -- but `String()` over an
   * arbitrary value produces "[object Object]", and a box somebody could type
   * past and save is the wrong place to discover that.
   */
  toDraft: (_field, value) => {
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    return ''
  },
  fromDraft: (_field, draft) => draft,
  Component: TextInput,
}

/**
 * A number, edited without its grouping marks.
 *
 * `formatNumberValue`'s third argument exists for exactly this: with the
 * thousand separators in, they jump around under the cursor as digits are
 * typed, because the grouping changes every third character. The prefix, the
 * suffix and the configured decimal mark stay, because `parseNumberValue`
 * reads them back and somebody editing a currency field expects to see the
 * currency.
 */
const numberEditor: CellEditorType = {
  toDraft: (field, value) => {
    if (value === null || value === undefined || value === '') return ''
    const raw = typeof value === 'string' || typeof value === 'number' ? value : null
    if (raw === null) return ''
    return formatNumberValue(field as FieldOf<'number'>, new BigNumber(raw), false) ?? ''
  },

  /**
   * `toFixed` and not `toString`: a `BigNumber` past fifteen digits or so
   * stringifies in exponential notation, which the backend reads as a different
   * number than the one somebody typed.
   */
  fromDraft: (field, draft) => {
    const parsed = parseNumberValue(field as FieldOf<'number'>, draft)
    return parsed === null ? null : parsed.toFixed()
  },

  Component: TextInput,
}

/**
 * A duration, edited in whichever of the ten formats the field is configured
 * for -- `1:30`, `1h 30m`, `90`, all of which mean the same thing.
 *
 * Rounded to the format on the way out rather than left as typed. The backend
 * rounds too, so sending the unrounded value means the cell changes under the
 * reader a moment after they leave it.
 */
const durationEditor: CellEditorType = {
  toDraft: (field, value) =>
    typeof value === 'number' || typeof value === 'string'
      ? formatDurationValue(parseDurationValue(value), durationFormatOf(field))
      : '',

  fromDraft: (field, draft) => {
    const seconds = parseDurationValue(draft, durationFormatOf(field))
    return seconds === null
      ? null
      : roundDurationValueToFormat(seconds, durationFormatOf(field))
  },

  Component: TextInput,
}

/**
 * The frame the controls sit in, so a checkbox and a text box occupy the same
 * rectangle over the cell.
 */
const FRAME =
  'flex h-full w-full items-center border-2 border-primary bg-background px-2 outline-none'

/**
 * A checkbox.
 *
 * The draft is still a string, because that is what a paste and a character
 * typed straight into the grid arrive as -- so typing "y" into a checkbox cell opens the editor
 * with the box already ticked, which falls out of the parser rather than
 * needing a case of its own.
 */
function CheckboxInput({
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  return (
    <div className={cn(FRAME, 'gap-1')} onKeyDown={onKeyDown}>
      <input
        type="checkbox"
        autoFocus={autoFocus}
        aria-label={label}
        checked={parseBooleanValue(value)}
        onChange={(event) => {
          onValueChange(String(event.target.checked))
        }}
        className="size-4 accent-primary"
      />
    </div>
  )
}

const booleanEditor: CellEditorType = {
  toDraft: (_field, value) => String(parseBooleanValue(value)),
  fromDraft: (_field, draft) => parseBooleanValue(draft),
  Component: CheckboxInput,
}

/**
 * A row of symbols, one of which is in the tab order.
 *
 * Roving focus rather than a tab stop per symbol: a rating out of ten would
 * otherwise be ten tab stops inside a single cell. The arrow keys move the
 * rating and the focus together, which is what a radio group does everywhere
 * else, and clicking the symbol a rating already stands on clears it -- the
 * only way to get back to nothing with the mouse.
 */
function RatingInput({
  field,
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  const { t } = useTranslation()
  const max = ratingMaxOf(field)
  const Icon = ratingIcon(field)
  const current = parseRating(value, max)
  // Nothing rated has no symbol to stand on, so the first one holds the focus.
  const standing = Math.max(current, 1)
  const focused = useRef<HTMLButtonElement>(null)
  useFocusOn(standing, autoFocus, () => focused.current)

  const set = (next: number) => {
    onValueChange(String(Math.min(Math.max(next, 0), max)))
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(FRAME, 'gap-0.5')}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
          event.preventDefault()
          set(current + 1)
          return
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
          event.preventDefault()
          set(current - 1)
          return
        }
        if (event.key === ' ') {
          // Chooses the symbol the focus is on, which is what a radio group
          // does. Left to the button it would *clear* a rating instead, since
          // the focus stands on the symbol already chosen.
          event.preventDefault()
          set(standing)
          return
        }
        onKeyDown(event)
      }}
    >
      {Array.from({ length: max }, (_, index) => index + 1).map((step) => (
        <button
          key={step}
          ref={step === standing ? focused : null}
          type="button"
          role="radio"
          aria-checked={step === current}
          aria-label={t('grid.ratingValue', { value: step, max })}
          tabIndex={step === standing ? 0 : -1}
          onClick={() => {
            set(step === current ? 0 : step)
          }}
        >
          <Icon
            className={cn('size-4', step > current && 'text-muted-foreground')}
            // The field's own colour, which is what the cell beneath draws in
            // too -- an editor in a different colour would read as a different
            // control rather than as the same one, opened.
            style={
              step <= current
                ? { color: ratingColor(field), fill: 'currentColor' }
                : undefined
            }
          />
        </button>
      ))}
    </div>
  )
}

const ratingEditor: CellEditorType = {
  toDraft: (field, value) => String(parseRating(value, ratingMaxOf(field))),
  fromDraft: (field, draft) => parseRating(draft, ratingMaxOf(field)),
  Component: RatingInput,
}

type SelectOption = NonNullable<FieldOf<'single_select'>['select_options']>[number]

/**
 * The field's options, minus any that cannot be referred to.
 *
 * `id` is optional in the schema because an option being *created* has none
 * yet. Every option that has come back from the API has one, and an option
 * that cannot be named by id is an option nothing can be set to.
 */
export const selectOptionsOf = (field: Field): (SelectOption & { id: number })[] =>
  ((field as FieldOf<'single_select'>).select_options ?? []).filter(
    (option): option is SelectOption & { id: number } => typeof option.id === 'number',
  )

/**
 * The option a piece of text names, by id and then by name.
 *
 * That order is the backend's, which builds the same map with the ids written
 * over the names -- so an option called "5" loses to the option whose id is 5,
 * on both sides.
 *
 * Text naming no option at all empties the cell rather than being sent on.
 * Sending it would fail the whole request with
 * `ERROR_INVALID_SELECT_OPTION_VALUES`, so a paste of two hundred rows where
 * one label was misspelled would write none of them -- and a paste that writes
 * nothing while reporting success is the worst of the three answers. Emptying
 * is what pasting "abc" into a number column already does.
 */
function resolveSelectOption(
  field: Field,
  draft: string,
): (SelectOption & { id: number }) | null {
  const text = draft.trim()
  if (text === '') return null

  const options = selectOptionsOf(field)
  return (
    options.find((option) => String(option.id) === text) ??
    options.find((option) => option.value === text) ??
    null
  )
}

/**
 * The options, as a list.
 *
 * A plain `<select>` rather than the design system's, and deliberately: the
 * grid commits an edit when focus leaves the editor, and a Radix popover
 * renders its options in a portal outside it -- so choosing one would read as
 * leaving the editor and commit what was there before the choice. A native
 * list is browser chrome, takes no focus at all, and brings its own keyboard
 * handling and typeahead.
 *
 * It costs the option colours, which the read-only cell will carry.
 */
function SelectInput({
  field,
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  const { t } = useTranslation()
  const chosen = resolveSelectOption(field, value)

  return (
    <select
      autoFocus={autoFocus}
      aria-label={label}
      value={chosen === null ? '' : String(chosen.id)}
      onChange={(event) => {
        onValueChange(event.target.value)
      }}
      onKeyDown={onKeyDown}
      className={cn(FRAME, 'text-sm')}
    >
      <option value="">{t('grid.noOption')}</option>
      {selectOptionsOf(field).map((option) => (
        <option key={option.id} value={String(option.id)}>
          {option.value}
        </option>
      ))}
    </select>
  )
}

const singleSelectEditor: CellEditorType = {
  toDraft: (_field, value) => {
    const id = (value as { id?: unknown } | null | undefined)?.id
    return typeof id === 'number' ? String(id) : ''
  },

  /**
   * The whole option, not its id.
   *
   * Both are accepted -- the serializer takes an id, a name or a serialized
   * option and reads the `id` out of the last -- and the option is the one that
   * is also the shape the *row* holds. The value sent is applied optimistically
   * before the answer comes back, so sending a bare id would put a number where
   * the cell expects an object and render nothing at all until the row arrived.
   */
  fromDraft: (field, draft) => resolveSelectOption(field, draft),
  Component: SelectInput,
}

/**
 * A date, typed rather than picked.
 *
 * The box is the whole editor, and that is a deliberate stopping point rather
 * than an unfinished one. A calendar would have to hang below the cell, and
 * the cell is inside the scroll container -- so it would be clipped at the
 * bottom of the viewport and would add to what the container scrolls. Solving
 * that means an editor that can be larger than its cell, which the row modal
 * needs as well and which is worth building once.
 *
 * Typing is also the faster path for anybody entering more than one date, and
 * the parser behind it is what a paste needs regardless.
 */
function DateInput({
  field,
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  const date = field as FieldOf<'date'>
  const shape = getDateHumanReadableFormat(date.date_format ?? 'ISO')

  return (
    <Input
      autoFocus={autoFocus}
      aria-label={label}
      // What to type, which is the one question this editor has to answer that
      // a text box does not. Only visible on a cell with nothing in it, which
      // is exactly when it is being asked.
      placeholder={
        date.date_include_time === true
          ? `${shape} ${getTimeHumanReadableFormat(date.date_time_format ?? '24')}`
          : shape
      }
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value)
      }}
      onKeyDown={onKeyDown}
      className="h-full w-full rounded-none border-2 border-primary bg-background px-2 text-sm shadow-none focus-visible:ring-0"
    />
  )
}

const dateEditor: CellEditorType = {
  toDraft: (field, value) => formatDateValue(field as FieldOf<'date'>, value),

  /**
   * Sent as the string the API stores, not as a `Date`.
   *
   * The same string is applied to the row before the answer comes back, and
   * the cell renders it by parsing it -- so a `Date` here would render
   * correctly until the moment it was serialised into a request body.
   */
  fromDraft: (field, draft) => {
    const date = field as FieldOf<'date'>
    const parsed = parseDateInput(date, draft)
    return parsed === null ? null : formatDateForApi(date, parsed)
  },

  Component: DateInput,
}

/**
 * Every option a piece of text names, in the field's own order.
 *
 * Split on commas, which is how upstream writes a multiple select to the
 * clipboard and how it reads one back. An option whose *name* contains a comma
 * cannot survive that round trip -- a real limitation, and the same one
 * upstream has, which matters because the text half of the clipboard is what
 * other applications see.
 *
 * A name it cannot place answers nothing, which no option's id can equal, so
 * it falls out of the answer without a step of its own. Dropping it rather
 * than sending it on is the reason a single select empties: an option the
 * field does not have fails the entire request, so one misspelled label in a
 * pasted column would write none of it.
 *
 * Ordered by the field rather than by the order they were ticked, because that
 * is the order the server sends them back in -- `SelectOption` orders by
 * `order` then `id` -- so the value shown before the answer arrives is the
 * value the answer brings.
 */
function resolveSelectOptions(
  field: Field,
  draft: string,
): (SelectOption & { id: number })[] {
  const wanted = new Set(
    draft.split(',').map((part) => resolveSelectOption(field, part)?.id),
  )
  return selectOptionsOf(field).filter((option) => wanted.has(option.id))
}

/**
 * A strip of chips, one of which holds the focus, any number of them chosen.
 *
 * The same roving-focus arrangement the rating uses, for the same reason: a
 * field with ten choices would otherwise be ten tab stops inside one cell. It
 * differs from the rating in the one way that matters -- there the focus and
 * the choice are the same thing, and here they are separate, because more than
 * one can be chosen. So the arrows move without choosing and the space bar
 * chooses what the focus is on, which is the listbox pattern rather than the
 * radio one.
 *
 * A strip inside the cell rather than a menu below it, and that is the same
 * stopping point the date editor records: an editor larger than its cell is
 * clipped by the scroll container it lives in, and solving that is work the row
 * modal needs as well.
 *
 * Shared by the two field types that are a list of choices -- a multiple select
 * and a list of people -- because the *arrangement* is the whole of what those
 * two have in common and it is the part with rules in it.
 */
function ChipStrip({
  choices,
  ticked,
  onToggle,
  onKeyDown,
  label,
  autoFocus,
}: {
  choices: readonly { id: number; label: string }[]
  ticked: ReadonlySet<number>
  onToggle: (id: number) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  label: string
  autoFocus: boolean
}): ReactElement {
  // The first chosen chip, so that opening a cell puts the focus on something
  // the reader can see is already ticked.
  const [focused, setFocused] = useState(() => {
    const first = choices.findIndex((choice) => ticked.has(choice.id))
    return first === -1 ? 0 : first
  })

  const chips = useRef<(HTMLButtonElement | null)[]>([])
  useFocusOn(focused, autoFocus, () => chips.current[focused] ?? null)

  const move = (by: number) => {
    setFocused((at) => Math.min(Math.max(at + by, 0), Math.max(choices.length - 1, 0)))
  }

  return (
    <div
      role="listbox"
      aria-multiselectable
      aria-label={label}
      className={cn(FRAME, 'gap-1 overflow-x-auto')}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault()
          move(1)
          return
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault()
          move(-1)
          return
        }
        if (event.key === ' ') {
          // Taken here rather than left to the button, so that the key means
          // the same thing as it does in the rating: choose what the focus is
          // on. Left alone it would also scroll the grid underneath.
          event.preventDefault()
          const choice = choices[focused]
          if (choice !== undefined) onToggle(choice.id)
          return
        }
        onKeyDown(event)
      }}
    >
      {choices.map((choice, index) => (
        <button
          key={choice.id}
          ref={(element) => {
            chips.current[index] = element
          }}
          type="button"
          role="option"
          aria-selected={ticked.has(choice.id)}
          tabIndex={index === focused ? 0 : -1}
          onClick={() => {
            setFocused(index)
            onToggle(choice.id)
          }}
          className={cn(
            'shrink-0 rounded-full border px-2 py-0.5 text-xs',
            ticked.has(choice.id)
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input text-muted-foreground',
          )}
        >
          {choice.label}
        </button>
      ))}
    </div>
  )
}

/** The ids in a comma-separated draft, as a set. */
const draftIds = (draft: string, resolve: (part: string) => number | undefined) =>
  new Set(
    draft
      .split(',')
      .map(resolve)
      .filter((id) => id !== undefined),
  )

/** A list of ids, as the draft that holds them. */
const idsAsDraft = (ids: readonly number[]) => ids.join(',')

/** The ids on a stored list of things that carry one. */
const storedIds = (value: unknown): number[] =>
  Array.isArray(value)
    ? value
        .map((item: unknown) => (item as { id?: unknown }).id)
        .filter((id) => typeof id === 'number')
    : []

function MultipleSelectInput({
  field,
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  const options = selectOptionsOf(field)
  const ticked = new Set(resolveSelectOptions(field, value).map((option) => option.id))

  return (
    <ChipStrip
      label={label}
      autoFocus={autoFocus}
      onKeyDown={onKeyDown}
      ticked={ticked}
      choices={options.map((option) => ({ id: option.id, label: option.value }))}
      onToggle={(id) => {
        const next = new Set(ticked)
        if (!next.delete(id)) next.add(id)
        onValueChange(
          idsAsDraft(
            options.filter((option) => next.has(option.id)).map((option) => option.id),
          ),
        )
      }}
    />
  )
}

const multipleSelectEditor: CellEditorType = {
  toDraft: (_field, value) => idsAsDraft(storedIds(value)),

  /**
   * An empty list, never null.
   *
   * Clearing every option is a real edit and the field holds a list, so the
   * way to say "none of them" is the empty one. `null` would be the field
   * having no answer, which a multiple select never has.
   */
  fromDraft: (field, draft) => resolveSelectOptions(field, draft),
  Component: MultipleSelectInput,
}

/**
 * The people a draft names, in the order the workspace lists them.
 *
 * By id and then by name, the way a select resolves an option, and for the
 * same two reasons: the draft the editor writes holds ids, and a paste from
 * anywhere else holds the names somebody read on screen.
 *
 * Somebody the workspace does not have answers nothing and falls out. Sending
 * them on fails the whole request -- `ERROR_INVALID_COLLABORATOR_IDS` -- so one
 * unrecognised name in a pasted column would write none of it.
 */
function resolveCollaborators(
  draft: string,
  context: FieldContext | undefined,
): { id: number }[] {
  const people = context?.collaborators ?? []

  const wanted = draftIds(draft, (part) => {
    const text = part.trim()
    return (
      people.find((person) => String(person.id) === text) ??
      people.find((person) => person.name === text)
    )?.id
  })

  return people.filter((person) => wanted.has(person.id)).map(({ id }) => ({ id }))
}

function CollaboratorInput({
  value,
  onValueChange,
  onKeyDown,
  label,
  context,
  autoFocus,
}: CellEditorProps): ReactElement {
  const people = context.collaborators ?? []
  const ticked = new Set(resolveCollaborators(value, context).map(({ id }) => id))

  return (
    <ChipStrip
      label={label}
      autoFocus={autoFocus}
      onKeyDown={onKeyDown}
      ticked={ticked}
      choices={people.map((person) => ({ id: person.id, label: person.name }))}
      onToggle={(id) => {
        const next = new Set(ticked)
        if (!next.delete(id)) next.add(id)
        onValueChange(
          idsAsDraft(
            people.filter((person) => next.has(person.id)).map(({ id: at }) => at),
          ),
        )
      }}
    />
  )
}

const collaboratorsEditor: CellEditorType = {
  toDraft: (_field, value) => idsAsDraft(storedIds(value)),

  /**
   * The ids alone, with no name beside them.
   *
   * A row stores the name that was current when it was written, and the
   * workspace's member list is the authority on what somebody is called now --
   * so the name in a row is read *through* the context and never out of the
   * value. Sending one would be inventing a fact the server ignores and the
   * screen does not read.
   */
  fromDraft: (_field, draft, context) => resolveCollaborators(draft, context),
  Component: CollaboratorInput,
}

/**
 * A link to rows of another table.
 *
 * The draft is the linked rows as JSON, which is a departure and a considered
 * one -- see `link-row.tsx`. A link row is chosen rather than typed, and what
 * is chosen has to carry the id the server is told and the text the cell shows.
 */
const linkRowEditor: CellEditorType = {
  toDraft: (_field, value) => JSON.stringify(normaliseLinks(value)),

  /**
   * The rows themselves, which the serializer reads the `id` out of.
   *
   * Sent whole rather than as ids because the same value is applied to the row
   * before the answer comes back, and a bare id renders as nothing: a link row
   * cell holds the related row's primary text and has no way to look one up.
   */
  fromDraft: (field, draft) => clipToOne(field, parseLinks(draft)),

  /**
   * A paste of text is a list of names, and only the server can place them.
   *
   * Sent as plain strings, which is the one shape the serializer resolves by
   * name. An object would have to carry an id, and an id is exactly what text
   * pasted from somewhere else does not have.
   */
  fromText: (field, text) =>
    clipToOne(
      field,
      text
        .split(',')
        .map((name) => name.trim())
        .filter((name) => name !== ''),
    ),

  Component: LinkRowInput,
  needsRoom: true,
}

/**
 * Cut to one link when the field holds one.
 *
 * The backend refuses the second outright, so a paste of two names into a
 * single-relationship field would write neither. Taking the first is what
 * upstream does and is the only answer that writes anything at all.
 */
function clipToOne<T>(field: Field, links: readonly T[]): T[] {
  const many = (field as FieldOf<'link_row'>).link_row_multiple_relationships !== false
  return many ? [...links] : links.slice(0, 1)
}

/**
 * The files attached to a row.
 *
 * The draft carries the whole files -- see `file.tsx` -- and what is sent is
 * the name and the name to show it under, which are the only two the backend
 * reads. Everything else in a file value is its own serialisation of what it
 * stored, and it fills that in again from the name.
 */
const fileEditor: CellEditorType = {
  toDraft: (_field, value) => JSON.stringify(normaliseFiles(value)),

  fromDraft: (_field, draft) =>
    parseFiles(draft).map(({ name, visible_name }) => ({
      name,
      visible_name: visible_name ?? '',
    })),

  /**
   * Pasted text is not a file, and there is nothing to be done with it.
   *
   * `undefined` means "no reading of this", and the column is left alone --
   * which matters more here than anywhere else: every other type can at least
   * empty a cell from text, and emptying a file cell would detach somebody's
   * attachments because a name landed in the wrong column.
   */
  fromText: () => undefined,

  Component: FileInput,
  needsRoom: true,
}

/**
 * What a password draft asks for.
 *
 * Three answers, and the backend already has words for all three: a string
 * sets the password, `null` removes it, and `true` is ignored -- which is
 * exactly "leave it alone". So the draft is the JSON of what will be sent, and
 * `true` is what an untouched editor holds.
 *
 * An empty box is nothing to set rather than a password to remove. Opening a
 * cell, typing and deleting is something people do constantly, and it must not
 * be the way a credential disappears -- removing one is its own control.
 *
 * Anything unreadable is also nothing. A character typed straight at the cell
 * arrives here as itself rather than as this editor's JSON, so the editor
 * opens empty and that character is lost. That is deliberate: a password is
 * not something to start setting by brushing the keyboard.
 */
function passwordIntent(draft: string): string | null | true {
  let asked: unknown
  try {
    asked = JSON.parse(draft)
  } catch {
    return true
  }

  if (asked === null) return null
  return typeof asked === 'string' && asked !== '' ? asked : true
}

/**
 * A new password, or none.
 *
 * The only field type whose value cannot be read back: it answers `true` when
 * one is set and nothing when one is not, and never what it is. So the box
 * always opens empty, and it opens empty whether or not there is a password
 * behind it -- there is no way to show what is there, and no honest difference
 * to draw between the two.
 */
function PasswordInput({
  value,
  onValueChange,
  onKeyDown,
  label,
  autoFocus,
}: CellEditorProps): ReactElement {
  const { t } = useTranslation()
  const asked = passwordIntent(value)

  return (
    <div className={cn(FRAME, 'gap-1')} onKeyDown={onKeyDown}>
      <Input
        autoFocus={autoFocus}
        type="password"
        // Nothing here is a password anybody has saved, and offering to fill
        // one in would put somebody else's credential into a table.
        autoComplete="new-password"
        aria-label={label}
        placeholder={t('password.new')}
        value={typeof asked === 'string' ? asked : ''}
        onChange={(event) => {
          onValueChange(JSON.stringify(event.target.value))
        }}
        className="h-full w-full rounded-none border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
      />
      <button
        type="button"
        aria-label={t('password.clear')}
        // Offered whether or not there is one to remove, because the editor
        // cannot be told: a cell answers that a password is set, and the
        // editor is given the draft rather than the cell.
        onClick={() => {
          onValueChange('null')
        }}
        className={cn(
          'shrink-0 rounded px-1 text-xs',
          asked === null
            ? 'bg-destructive text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted',
        )}
      >
        <X className="size-3" />
      </button>
    </div>
  )
}

const passwordEditor: CellEditorType = {
  /** Always the same, because a password is never readable. */
  toDraft: () => 'true',

  fromDraft: (_field, draft) => passwordIntent(draft),

  /**
   * Pasted text is not a password anybody meant to set.
   *
   * The same answer a file field gives, for a sharper reason: a word landing
   * in the wrong column would change a credential silently, and nothing on
   * screen could show that it had. A copy from this application carries `true`
   * instead, which the backend ignores by design.
   */
  fromText: () => undefined,

  Component: PasswordInput,
}

const EDITORS = {
  text: textEditor,
  long_text: textEditor,
  url: textEditor,
  email: textEditor,
  phone_number: textEditor,

  number: numberEditor,
  duration: durationEditor,
  rating: ratingEditor,
  boolean: booleanEditor,
  single_select: singleSelectEditor,
  date: dateEditor,
  multiple_select: multipleSelectEditor,
  multiple_collaborators: collaboratorsEditor,
  link_row: linkRowEditor,
  file: fileEditor,
  password: passwordEditor,

  // Read-only by their nature: the server computes them and an editor would
  // have nothing to send. Every `null` below is one of those -- there are no
  // types left that are anybody's to write.
  formula: null,
  count: null,
  rollup: null,
  lookup: null,
  autonumber: null,
  uuid: null,
  created_on: null,
  created_by: null,
  last_modified: null,
  last_modified_by: null,

  // Not a field on a table at all -- it belongs to a form view's row editor.
  form_view_edit_row: null,
} satisfies Record<FieldTypeName, CellEditorType | null>

/**
 * The editor for a field, or null when it cannot be edited here.
 *
 * `read_only` is checked first and covers more than the map's own nulls: a text
 * field can be read-only too, when a field rule or a data sync owns its values.
 */
export function editorFor(field: Field): CellEditorType | null {
  if (field.read_only) return null
  return EDITORS[field.type]
}
